import {getUniqueEntityId} from "./entities";

/**
 * @example data used in examples:
 * [
 *    {tlp: "green", severity: "high"},
 *    {tlp: "amber", severity: "high"},
 *    {tlp: "green", severity: "low"}
 *  ]
 */

/**
 * Creates an array with arrays grouped by property value
 *
 * @param {Array} arr array of objects
 * @param {string} property property key that you want to group by
 * @returns {Array}
 * @example
    [
      high: [{"tlp": "green", "severity": "high"}],
      low:  [{"tlp": "amber", "severity": "low"}, {"tlp": "green", "severity": "low"}]
    ]
 */
export const makeArrayByKey = (arr, property) => {
  return arr.reduce((acc, object) => {
    const key = object[property];
    acc[key] = acc[key] || [];
    acc[key].push(object);
    return acc;
  }, []);
};

/**
 * Creates an object with arrays grouped by property value
 *
 * @param {Array} arr array of objects
 * @param {string} property property key that you want to group by
 * @returns {Object}
 * @example
    {
      high: [{"tlp": "green", "severity": "high"}],
      low:  [{"tlp": "amber", "severity": "low"}, {"tlp": "green", "severity": "low"}]
    }
 */
export const makeObjectWithArraysByKey = (arr, property) => {
  return arr.reduce((acc, object) => {
    (acc[object[property]] = acc[object[property]] || []).push(object);
    return acc;
  }, {});
};

/**
 * Creates an object with arrays grouped by property value and adds a unique id field
 *
 * @param {Array} arr array of objects
 * @param {string} property property key that you want to group by
 * @param {string} entityType type from @constant ENTITY_TYPE
 * @returns {Object}
 */
export const makeEntityObjectWithArraysByKey = (arr, property, entityType) => {
  return arr.reduce((acc, object) => {
    (acc[object[property]] = acc[object[property]] || []).push({
      ...object,
      id: getUniqueEntityId(entityType, object)
    });
    return acc;
  }, {});
};

/**
 * Combines arrays of objects and filters out duplicates by a unique field.
 *
 * @param {Array<object>} arr1 first array of objects
 * @param {Array<object>} arr2 second array of objects
 * @param {string} fieldName unique field name to filter out duplicates with (e.g. id)
 * @returns {Array<object>} de-duped combined array of objects
 */
export const combineArraysOfObjects = (arr1 = [], arr2 = [], fieldName) => {
  const uniqueValues =
    arr1.length > 0 ? new Set(arr1.map((d) => d[fieldName])) : [];
  return [...arr1, ...arr2.filter((d) => !uniqueValues.has(d[fieldName]))];
};

/**
 * Turns an array of objects into an object using the specified field as keys
 *
 * @param {Array<object>} array
 * @param {string} fieldName field name in the objects
 * @param {Function} transform Optional transform function
 * @returns {object} object with indicated field as keys
 */
export const makeObjectFromArray = (array, fieldName, transform = null) =>
  array.reduce(
    (acc, object) => ({
      ...acc,
      [transform ? transform(object[fieldName]) : object[fieldName]]: object
    }),
    {}
  );
